import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, X, Check, FileText, Cpu, RefreshCw, Rocket, Stethoscope, Home, Building2, Car } from 'lucide-react';
import CountUp from '../../components/CountUp';

const oldWay = [
  '$10,000–$50,000 production cost',
  '6–12 weeks from brief to air',
  'Requires studio, crew, talent',
  'One version — no iterations',
  'Creative not tied to targeting data'
];

const newWay = [
  'Starting from under $500 per creative',
  'Broadcast-ready spot in minutes',
  'AI voiceover, music, dynamic text — no crew',
  'Multiple variants — real-time revisions',
  'Industry templates matched to audience targeting'
];

const flow = [
  { num: '01', icon: <FileText className="w-6 h-6" />, title: 'Input', desc: "Drop in your client's URL or write a 2-line brief. Industry, offer, target audience." },
  { num: '02', icon: <Cpu className="w-6 h-6" />, title: 'AI Generation', desc: 'Our engine assembles visuals, voiceover, music and dynamic text matched to the brief.' },
  { num: '03', icon: <RefreshCw className="w-6 h-6" />, title: 'Review & Revise', desc: 'Your client sees multiple variants instantly. Revisions happen in real time — no waiting.' },
  { num: '04', icon: <Rocket className="w-6 h-6" />, title: 'Approve & Launch', desc: 'Broadcast-ready files delivered under your brand. Campaign goes live immediately.' }
];

const templates = [
  { icon: <Stethoscope className="w-6 h-6" />, name: 'Dental & Med Spa', audience: 'Adults 28–55, HHI $75K+', geo: 'Within 8mi of each location', approach: 'Before/after format, offer-led CTA, appointment booking flow', outcome: "High LTV patients justify TV. Competitors aren't there yet." },
  { icon: <Home className="w-6 h-6" />, name: 'Home Services', audience: 'Homeowners 30–65', geo: 'Within 15mi radius', approach: 'Seasonal offers, trust-building creative, local identity', outcome: 'HVAC, roofing, pest control — high margin, repeat customers.' },
  { icon: <Building2 className="w-6 h-6" />, name: 'Real Estate', audience: 'Move-intent households', geo: 'Geo by listing zone', approach: 'Listing showcase, agent brand, open house CTA', outcome: 'TV credibility at 10% of traditional broadcast cost.' },
  { icon: <Car className="w-6 h-6" />, name: 'Auto Dealerships', audience: 'In-market buyers 25–55', geo: 'Local DMA targeting', approach: 'Model highlights, financing offers, test drive CTA', outcome: 'Dealers already buy TV. CTV is measurable, targeted, cheaper.' }
];

const stats = [
  { value: '94%', label: 'Average video completion rate across MarqiCast campaigns' },
  { value: '4+', label: 'Creative variants generated per brief — choose the best' },
  { value: '86%', label: 'Of ad buyers now use AI for video — IAB 2025' }
];

const CastCreativePage = () => {
  return (
    <div className="min-h-screen" data-testid="cast-creative-page">
      <Helmet><title>MarqiCast — AI Video Creative</title></Helmet>

      {/* Hero */}
      <section className="py-24 md:py-32 text-center">
        <div className="container max-w-4xl">
          <h1 className="display-md text-[var(--text-primary)] mb-6" data-testid="cast-creative-hero-title">
            Broadcast-Quality CTV Ads.
            <br /><span className="text-[var(--accent-primary)]">In Minutes. Not Months.</span>
          </h1>
          <p className="body-lg mb-12 max-w-3xl mx-auto">
            The #1 reason SMBs never advertise on TV is production cost and complexity. MarqiCast
            eliminates both. Generate a 30-second broadcast-ready spot from a URL or a brief — AI
            voiceover, music, dynamic text — under your agency's brand.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact" className="btn-primary" data-testid="cast-creative-cta-primary">
              Generate a Sample Ad
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <a href="#templates" className="btn-secondary">See Creative Examples</a>
          </div>
        </div>
      </section>

      {/* Old way vs MarqiCast way */}
      <section className="py-24 bg-[var(--bg-secondary)]">
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16 max-w-3xl mx-auto">TV Production Doesn't Have to Take 3 Months.</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="feature-card">
              <h3 className="h3 text-[var(--text-muted)] mb-6">Traditional TV Production</h3>
              <ul className="space-y-4">
                {oldWay.map((item) => (
                  <li key={item} className="flex items-start space-x-3">
                    <X className="w-5 h-5 text-red-400/80 flex-shrink-0 mt-0.5" />
                    <span className="text-[var(--text-muted)] line-through decoration-[var(--border-primary)]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="feature-card border-[var(--accent-primary)]">
              <h3 className="h3 text-[var(--accent-primary)] mb-6">MarqiCast AI Creative</h3>
              <ul className="space-y-4">
                {newWay.map((item) => (
                  <li key={item} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                    <span className="text-[var(--text-secondary)]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How it works flow */}
      <section className="py-24">
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16 max-w-3xl mx-auto">From Brief to Broadcast in 4 Steps.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {flow.map((s, i) => (
              <div key={s.num} className="feature-card animate-fade-in" style={{ animationDelay: `${i * 0.12}s` }}>
                <div className="flex items-center justify-between mb-6">
                  <span className="text-3xl font-bold text-[var(--accent-primary)]">{s.num}</span>
                  <div className="w-10 h-10 rounded-full bg-[var(--accent-bg)] flex items-center justify-center text-[var(--accent-primary)]">{s.icon}</div>
                </div>
                <h3 className="h3 text-[var(--text-primary)] mb-3">{s.title}</h3>
                <p className="body-md">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry templates */}
      <section id="templates" className="py-24 bg-[var(--bg-secondary)]">
        <div className="container">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">Ready-to-Launch Templates for Your Best-Fit Verticals.</h2>
            <p className="body-lg">Every template is built around real audience behavior in that vertical — not generic ad formats repurposed from social.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {templates.map((t, i) => (
              <div key={t.name} className="feature-card hover-lift animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="w-12 h-12 rounded-full bg-[var(--accent-bg)] flex items-center justify-center text-[var(--accent-primary)] mb-6">{t.icon}</div>
                <h3 className="h3 text-[var(--text-primary)] mb-4">{t.name}</h3>
                <div className="space-y-3 text-sm">
                  <div><span className="text-[var(--accent-primary)] font-semibold">Audience: </span><span className="text-[var(--text-secondary)]">{t.audience}</span></div>
                  <div><span className="text-[var(--accent-primary)] font-semibold">Geo: </span><span className="text-[var(--text-secondary)]">{t.geo}</span></div>
                  <div><span className="text-[var(--accent-primary)] font-semibold">Creative: </span><span className="text-[var(--text-secondary)]">{t.approach}</span></div>
                </div>
                <p className="text-sm text-[var(--text-muted)] mt-4 pt-4 border-t border-[var(--border-subtle)]">{t.outcome}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats + CTA */}
      <section className="py-24">
        <div className="container">
          <h2 className="h1 text-[var(--text-primary)] text-center mb-16">Creative That Actually Performs.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {stats.map((s, i) => (
              <div key={s.label} className="text-center animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                <CountUp value={s.value} className="block text-6xl font-bold text-[var(--accent-primary)] mb-4" />
                <p className="body-md max-w-xs mx-auto">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="feature-card text-center max-w-4xl mx-auto">
            <h2 className="h2 text-[var(--text-primary)] mb-6">Ready to Generate Your First CTV Ad?</h2>
            <p className="body-lg mb-12 max-w-2xl mx-auto">Book a strategy session and we'll generate a sample creative for one of your existing clients — live, during the call.</p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact" className="btn-primary glow-effect">Generate a Sample Ad<ArrowRight className="ml-2 w-5 h-5" /></Link>
              <Link to="/contact" className="btn-secondary">Book Strategy Session</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CastCreativePage;
